/**
 * The live certificate preview and its zoom / download / print / share strip.
 *
 * The certificate's colours are hardcoded hex rather than theme tokens. That is
 * deliberate and matches the prototype: this is a printable document with its own
 * fixed palette, and it must NOT follow the app's theme — a light theme would
 * otherwise produce an unreadable certificate. Georgia is likewise the document's
 * face, not the app's.
 *
 * Zoom is two discrete states (100% / 115%), not a continuous control, which is why
 * the buttons set a boolean rather than stepping a number.
 */
import { Download, ExternalLink, Printer } from 'lucide-react';
import commercialLicenseCertificatePreview from '../../assets/commercial-license-certificate-preview.png';
import type { CertificateFields } from '../../lib/license';
import { Btn } from '../ui';
import { c } from '../../styles/theme';
import CertificateDocument from './CertificateDocument';

/** Small square +/− control beside the certificate. */
function ZoomButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label === '+' ? 'Zoom in' : 'Zoom out'}
      style={{
        width: 26,
        height: 26,
        borderRadius: 6,
        background: c.surface2,
        border: `1px solid ${c.border}`,
        cursor: 'pointer',
        color: c.text2,
        fontSize: 14,
        lineHeight: 1,
      }}
    >
      {label}
    </button>
  );
}

export interface CertificatePreviewProps {
  version: string;
  fields: CertificateFields;
  zoomed: boolean;
  onZoomChange: (zoomed: boolean) => void;
  onDownload: () => void;
  onPrint: () => void;
  onShare: () => void;
}

export default function CertificatePreview({
  version,
  fields,
  zoomed,
  onZoomChange,
  onDownload,
  onPrint,
  onShare,
}: CertificatePreviewProps) {
  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <p style={{ fontSize: 12, color: c.text3, margin: 0 }}>Live certificate preview</p>
        <span
          style={{
            fontSize: 10,
            color: c.text3,
            background: c.surface2,
            border: `1px solid ${c.border}`,
            borderRadius: 999,
            padding: '2px 8px',
          }}
        >
          v{version}
        </span>
      </div>
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'none' }} aria-hidden="true">
          <img src={commercialLicenseCertificatePreview} alt="" />
        </div>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <CertificateDocument fields={fields} scale={zoomed ? 1.15 : 1} />
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 18,
          flexWrap: 'wrap',
        }}
      >
        <ZoomButton label="−" onClick={() => onZoomChange(false)} />
        <span style={{ fontSize: 12, color: c.text2, minWidth: 40, textAlign: 'center' }}>
          {zoomed ? '115%' : '100%'}
        </span>
        <ZoomButton label="+" onClick={() => onZoomChange(true)} />
        <Btn variant="secondary" onClick={onDownload}>
          <Download size={13} />
          Download
        </Btn>
        <Btn variant="secondary" onClick={onPrint}>
          <Printer size={13} />
          Print / Save as PDF
        </Btn>
        <Btn variant="secondary" onClick={onShare}>
          <ExternalLink size={13} />
          Share
        </Btn>
      </div>
    </>
  );
}
